import { css } from '@eti/styles';
import { CaretRight, Circle } from '@phosphor-icons/react';
import type { AlignTypes } from '@etg/wings';
import { breakpoints, Inline, Stack, Text, textVariants, vars } from '@etg/wings';
import { TripType } from '@eti/schema-types';
import { CarrierLogo } from '@eti/components';
import { date } from '@eti/utils';
import { useDateFormat, useDirection, useTranslation } from '@eti/providers';
import { forwardRef } from 'react';
import type { BoundType } from '../../../../models';
import ClickableCard from '../../../../../../common/components/ClickableCard';
import { getBoundTitle, getBoundType } from '../../../../../../widgets/trip-details/utils/format';
import { useOrderDetails } from '../../../../containers/OrderDetailsContext';
import { screenReadersOnlyStyles } from '../../../../../../utils/styleUtils';
import {
  getLatestBoundInfo,
  getNumberOfTechnicalStops,
  getOriginalBoundInfo,
  getUniqueMarketingCarriersFromSegments,
} from './utils';
import TripIcons from './TripIcons';
import Time from './Time';
import TripInfo from './TripInfo';

const dividerStyles = css`
  flex: 1;
  position: relative;

  @media (min-width: ${breakpoints._768}) {
    height: 32px;
  }

  &::before {
    border-top: 1px dotted ${vars.colors.divider};
    content: '';
    position: absolute;
    top: 50%;
    width: 100%;
  }
`;

const locationStyles = (alignment?: AlignTypes) => css`
  text-align: ${alignment};
  width: 90px;

  @media (min-width: ${breakpoints._560}) {
    width: 150px;
  }

  @media (min-width: ${breakpoints._768}) {
    width: 90px;
  }

  @media (min-width: ${breakpoints._970}) {
    width: 150px;
  }
`;

const squareStyles = css`
  background-color: #fff;
  border: solid 1px #8c8c8c;
  color: #8c8c8c;
  height: 24px;
  position: relative;
  text-align: center;
  width: 24px;
  z-index: 1;
`;

const flexStyles = css`
  flex: 1;
`;

const fullHeightStyles = css`
  height: 100%;
`;

const iconWrapperStyles = css`
  display: none;

  @media (min-width: ${breakpoints._768}) {
    display: flex;
    padding-inline-end: 16px;
  }
`;

const carrierLogoInfoStyles = css`
  background-color: #fff;
  height: 24px;
  width: 24px;
  z-index: 1;
`;

const departureDateStyles = css`
  gap: 0 8px;
`;

const stackStyles = css`
  flex: 1;
  padding: 16px;
  padding-inline-end: 16px;

  @media (min-width: ${breakpoints._768}) {
    padding-inline-end: 0;
  }
`;

const latestDepartureDateStyles = (isDateChanged: boolean) => css`
  background-color: ${isDateChanged ? vars.colors.signal.caution.tint : 'none'};
`;

const originalDepartureDateStyles = css`
  color: #737373;
  text-decoration: line-through;
`;

interface BoundCardProps {
  ariaControls?: string;
  ariaExpanded?: 'true' | 'false';
  ariaHaspopup?: 'dialog' | 'menu' | 'listbox' | 'tree' | 'grid';
  bound: NonNullable<BoundType>;
  boundIndex: number;
  onBoundCardSelection: () => void;
}

const BoundCard = forwardRef<HTMLButtonElement, BoundCardProps>(
  ({ bound, boundIndex, onBoundCardSelection, ariaControls, ariaExpanded, ariaHaspopup }, ref?) => {
    const { isRTL } = useDirection();
    const { t } = useTranslation();
    const { dayYear } = useDateFormat();
    const { journeyType } = useOrderDetails();

    const {
      arrivedAt,
      arrivalCode,
      departuredAt,
      departureCode,
      destination,
      duration,
      origin,
      numberOfStops,
      uniqueEventTypes,
    } = getLatestBoundInfo(bound.segments);

    const originalBoundInfo = bound.originalVersion
      ? getOriginalBoundInfo(bound.originalVersion)
      : null;

    const durationInDays = date.durationInDays(new Date(departuredAt), new Date(arrivedAt));

    const boundTitle = t(
      getBoundTitle(getBoundType(boundIndex), journeyType === TripType.MultiStop),
      parseInt(bound.boundId, 10) + 1,
    );
    const latestDepartureDate = date.format(departuredAt, dayYear);
    const originalDepartureDate = originalBoundInfo
      ? date.format(originalBoundInfo.departuredAt, dayYear)
      : null;
    const isDepartureDateChanged =
      originalDepartureDate && latestDepartureDate !== originalDepartureDate;
    const marketingCarriers = getUniqueMarketingCarriersFromSegments(bound.segments);
    const allMarketingCarriersText = marketingCarriers
      .map((marketingCarrier) => marketingCarrier.name)
      .join(', ');

    return (
      <Stack className={fullHeightStyles} role="listitem" spacing={8}>
        <ClickableCard
          aria-controls={ariaControls}
          aria-expanded={ariaExpanded}
          aria-haspopup={ariaHaspopup}
          aria-labelledby={`bound-${boundIndex}-title bound-${boundIndex}-new-date bound-${boundIndex}-old-date bound-${boundIndex}-departure-new-time bound-${boundIndex}-departure-old-time bound-${boundIndex}-departure-city bound-${boundIndex}-arrival-new-time bound-${boundIndex}-arrival-old-time bound-${boundIndex}-arrival-city bound-${boundIndex}-carriers bound-${boundIndex}-duration bound-${boundIndex}-stops bound-${boundIndex}-technical-stops bound-${boundIndex}-trip-events`}
          className={flexStyles}
          data-testid={`itinerary-overview-bound-${bound.boundId}-card`}
          onCardSelection={onBoundCardSelection}
          ref={ref}
        >
          <Inline aria-hidden="true" className={fullHeightStyles} noWrap spacing={8}>
            <Stack className={stackStyles} spacing={8}>
              <Inline alignY="center" className={departureDateStyles}>
                <Text id={`bound-${boundIndex}-title`} variant={textVariants._16}>
                  {boundTitle}
                </Text>
                <Circle color="#737373" size={8} weight="fill" />
                <Inline noWrap spacing={8}>
                  {isDepartureDateChanged && (
                    <>
                      <span className={screenReadersOnlyStyles} id={`bound-${boundIndex}-old-date`}>
                        {t('ScreenReader.ItineraryButton.OldDate.Text', originalDepartureDate)}
                      </span>
                      <Text className={originalDepartureDateStyles} variant={textVariants._16}>
                        {originalDepartureDate}
                      </Text>
                    </>
                  )}
                  <span className={screenReadersOnlyStyles} id={`bound-${boundIndex}-new-date`}>
                    {isDepartureDateChanged
                      ? t('ScreenReader.ItineraryButton.NewDate.Text', latestDepartureDate)
                      : latestDepartureDate}
                  </span>
                  <Text
                    className={latestDepartureDateStyles(isDepartureDateChanged)}
                    variant={textVariants._16}
                  >
                    {latestDepartureDate}
                  </Text>
                </Inline>
              </Inline>
              <Inline alignY="top" noWrap spacing={0}>
                <Stack spacing={4}>
                  <Time
                    alignment="start"
                    boundIndex={boundIndex}
                    data-testid={`itinerary-overview-bound-${bound.boundId}-departure-time`}
                    dateOfTrip={departuredAt}
                    locationType="Departure"
                    originalDateOfTrip={originalBoundInfo?.departuredAt}
                  />
                  <span
                    className={screenReadersOnlyStyles}
                    id={`bound-${boundIndex}-departure-city`}
                  >
                    {t(
                      'ScreenReader.ItineraryButton.Departure.Location.Text',
                      origin,
                      departureCode,
                    )}
                  </span>
                  <Text
                    className={locationStyles('start')}
                    data-testid={`itinerary-overview-bound-${bound.boundId}-departure-city`}
                    id="departure-city"
                  >
                    {`${origin} (${departureCode})`}
                  </Text>
                </Stack>
                <Stack className={flexStyles} spacing={4}>
                  <Inline
                    align="center"
                    alignY="center"
                    className={dividerStyles}
                    noWrap
                    spacing={4}
                  >
                    {marketingCarriers
                      .slice(0, 2)
                      .map(
                        (marketingCarrier) =>
                          marketingCarrier.code &&
                          marketingCarrier.name && (
                            <CarrierLogo
                              className={carrierLogoInfoStyles}
                              data-testid={`carrier-logo-${marketingCarrier.code}`}
                              id={marketingCarrier.code}
                              imageAlt={marketingCarrier.name}
                              key={marketingCarrier.code}
                            />
                          ),
                      )}
                    {marketingCarriers.length > 2 && (
                      <div className={squareStyles} data-testid="number-of-hidden-carriers">
                        <Text variant={textVariants._12}>+{marketingCarriers.length - 2}</Text>
                      </div>
                    )}
                  </Inline>
                  <span className={screenReadersOnlyStyles} id={`bound-${boundIndex}-carriers`}>
                    {t('ScreenReader.ItineraryButton.Carriers.Text', allMarketingCarriersText)}
                  </span>
                  <TripInfo
                    boundIndex={boundIndex}
                    duration={duration}
                    numberOfStops={numberOfStops}
                    numberOfTechnicalStops={getNumberOfTechnicalStops(bound.segments)}
                  />
                </Stack>
                <Stack align="end" spacing={4}>
                  <Time
                    alignment="end"
                    boundIndex={boundIndex}
                    data-testid={`itinerary-overview-bound-${bound.boundId}-arrival-time`}
                    dateOfTrip={arrivedAt}
                    durationInDays={durationInDays}
                    locationType="Arrival"
                    originalDateOfTrip={originalBoundInfo?.arrivedAt}
                    originalDurationInDays={originalBoundInfo?.durationInDays}
                  />
                  <span className={screenReadersOnlyStyles} id={`bound-${boundIndex}-arrival-city`}>
                    {t(
                      `ScreenReader.ItineraryButton.Arrival.Location.Text`,
                      destination,
                      arrivalCode,
                    )}
                  </span>
                  <Text
                    className={locationStyles('end')}
                    data-testid={`itinerary-overview-bound-${bound.boundId}-arrival-city`}
                  >
                    {destination} ({arrivalCode})
                  </Text>
                </Stack>
              </Inline>
              {uniqueEventTypes.length > 0 && (
                <TripIcons boundIndex={boundIndex} eventTypes={uniqueEventTypes} />
              )}
            </Stack>
            <Inline align="center" alignY="center" className={iconWrapperStyles}>
              <CaretRight
                aria-hidden="true"
                data-testid={`itinerary-overview-bound-${bound.boundId}-caret-icon`}
                mirrored={isRTL}
                size={24}
              />
            </Inline>
          </Inline>
        </ClickableCard>
      </Stack>
    );
  },
);

export default BoundCard;
